import { useNavigate, useParams } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Alert from "../../components/common/Alert";
import Badge from "../../components/common/Badge";
import PageHeader from "../../components/common/PageHeader";
import { useGetLocalDevicesQuery, useGetTillQuery } from "../../store/api";
import { getCashingUpStatusTone } from "../../utils/cashingUp";

const DetailMetric = ({ label, value, className = "" }) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800/70 ${className}`}
  >
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {label}
    </p>
    <p className="mt-2 text-xl font-bold text-slate-900 dark:text-slate-100">
      {value}
    </p>
  </div>
);

const TillDetail = () => {
  const { tillId } = useParams();
  const navigate = useNavigate();
  const {
    data: till,
    isLoading,
    isError,
    error,
  } = useGetTillQuery(tillId, { skip: !tillId });
  const { data: localDevicesData } = useGetLocalDevicesQuery();

  const localDevices = Array.isArray(localDevicesData) ? localDevicesData : [];
  const deviceById = new Map(
    localDevices.map((device) => [device.deviceId, device]),
  );
  const linkedDevice = deviceById.get(till?.deviceId);
  const linkedDeviceLabel =
    linkedDevice?.deviceName ||
    linkedDevice?.deviceKey ||
    linkedDevice?.deviceId ||
    "Not assigned";

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Till Details" subtitle="Loading till..." />
        <Card>
          <p className="text-sm text-slate-500">Loading till details...</p>
        </Card>
      </div>
    );
  }

  if (isError || !till) {
    return (
      <div className="space-y-6">
        <PageHeader title="Till Details" subtitle="Unable to load till" />
        <Card className="space-y-4">
          <Alert tone="error">
            {error?.data?.detail ||
              error?.data?.message ||
              "Failed to load till details."}
          </Alert>
          <Button size="sm" variant="secondary" onClick={() => navigate("/cash-management/tills")}>
            Back to Tills
          </Button>
        </Card>
      </div>
    );
  }

  const lastSyncedAt = till.lastSyncedAt
    ? new Date(till.lastSyncedAt).toLocaleString()
    : "Not synced yet";

  return (
    <div className="space-y-6">
      <PageHeader
        title={till.tillName || "Till Details"}
        subtitle="View till configuration and operational setup."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() =>
                navigate("/cash-management/tills", {
                  state: { editTillId: till.tillId },
                })
              }
            >
              Edit Till
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate("/cash-management/tills")}
            >
              Back to Tills
            </Button>
          </div>
        }
      />

      <Card className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <DetailMetric label="Linked Device" value={linkedDeviceLabel} />
          <DetailMetric label="Terminal" value={till.terminalId || "Not assigned"} />
          <DetailMetric
            label="Sync Status"
            value={
              till.syncStatus ? (
                <Badge tone={getCashingUpStatusTone(till.syncStatus)}>
                  {till.syncStatus}
                </Badge>
              ) : (
                "Unknown"
              )
            }
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <DetailMetric
            label="Blind Count"
            value={till.blindCountEnabled ? "Enabled" : "Disabled"}
          />
          <DetailMetric
            label="Allow Any Cashier"
            value={till.allowAnyCashier ? "Yes" : "No"}
          />
          <DetailMetric label="Last Synced" value={lastSyncedAt} />
        </div>
      </Card>
    </div>
  );
};

export default TillDetail;
